import { useEffect, useRef, useState } from 'react';
import { CalendarPlus, CheckCircle2, ChevronRight } from 'lucide-react';
import { onboardingAPI } from '../../services/api';
import { Modal, fmtCount } from './DashboardPrimitives';
import { InlineLoader } from '../loading';
import {
  ADVICE_META, BAND_META, DAY_STATE_LABEL, MAX_DECISION_NOTE, TONE_PILL,
  formatOnScale, formatPct, formatScore, scorePill, shortDate,
} from './onboardingScore';
import type { JoinerScore, ScoreAdvice, ScoreDay, ScoreDecision, ScoreDecisionKind } from './onboardingScore';

/**
 * Dashboard → Onboarding → a joiner's onboarding score.
 *
 * Opened from the worklist's score column. Shows how the buddy rated each shadow
 * day (one row per sign-off day, expandable to the individual ratings), what the
 * score advises once shadow training is over, and every decision recorded so far.
 *
 * The system only ADVISES. A coordinator records the call here: "Proceed to
 * deployment" is recorded and nothing else changes; "Extend onboarding" also
 * pushes the plan end, exactly as the Extend action does. Decisions are
 * append-only, so recording again adds to the history rather than replacing it.
 */

export interface OnboardingScoreModalProps {
  projectId: number;
  joiner: { employeeCode: string; employeeName: string; dateOfJoining: string | null; planEndDate: string | null };
  onClose: () => void;
  /** After a decision is recorded; the tab then refreshes its rows and scores. */
  onDecided: (result: { decision: ScoreDecision; score: JoinerScore }) => void;
}

type DecisionResult = { decision: ScoreDecision; score: JoinerScore };

// ── Helpers ──────────────────────────────────────────────────────────────────

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

/** Today in IST as YYYY-MM-DD (en-CA formats as ISO). */
const todayIst = (): string => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

/** YYYY-MM-DD plus n calendar days. Worked in UTC so no offset can shift the day. */
const addDaysIso = (iso: string, n: number): string => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

/** When a decision was recorded, as its IST calendar day, e.g. "25 Sept 2026". */
const fmtDecidedOn = (ts: string): string => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return shortDate(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const DECISION_ACTION: Record<ScoreDecisionKind, string> = {
  deploy: 'Proceed to deployment',
  extend: 'Extend onboarding',
};

/** A recorded decision in a few words — the same wording as the worklist pill. */
const decisionHeadline = (d: ScoreDecision): string =>
  d.decision === 'deploy'
    ? 'Cleared for deployment'
    : d.extendedTo ? `Extended to ${shortDate(d.extendedTo)}` : 'Extended';

/**
 * Why this choice needs a note, or null when it doesn't. Same rule as the API: a
 * note is required whenever the choice differs from the advice — including when
 * the score could not advise either way.
 */
const noteReason = (choice: ScoreDecisionKind, advice: ScoreAdvice | null): string | null => {
  if (advice === choice) return null;
  if (advice === 'deploy' || advice === 'extend') return `This goes against the advice (${ADVICE_META[advice].label}).`;
  const label = advice ? ` (${ADVICE_META[advice].label})` : '';
  return `The score gives no deploy or extend advice here${label}, so say what this is based on.`;
};

/**
 * Show the decision's own score straight away. It may be the list shape (no
 * per-question ratings, no history), so keep what the detail view already had
 * until the refresh lands.
 */
const mergeDecided = (prev: JoinerScore | null, r: DecisionResult): JoinerScore => {
  const questions = new Map((prev?.days ?? []).map((d) => [d.date, d.questions]));
  return {
    ...r.score,
    days: r.score.days.map((d) => (d.questions ? d : { ...d, questions: questions.get(d.date) })),
    latestDecision: r.score.latestDecision ?? r.decision,
    decisions: r.score.decisions ?? [r.decision, ...(prev?.decisions ?? []).filter((x) => x.id !== r.decision.id)],
  };
};

const dayKey = (d: ScoreDay) => `${d.date}-${d.shadowDay ?? d.journeyDay}`;

// ── Pieces ───────────────────────────────────────────────────────────────────

function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Pill({ tone, title, children }: { tone: keyof typeof TONE_PILL; title?: string; children: React.ReactNode }) {
  return (
    <span title={title} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${TONE_PILL[tone]}`}>
      {children}
    </span>
  );
}

function DayRows({ day, passMark, today, open, onToggle }: {
  day: ScoreDay; passMark: number; today: string; open: boolean; onToggle: () => void;
}) {
  const questions = day.state === 'rated' ? day.questions ?? [] : [];
  const canExpand = questions.length > 0;
  const label = day.shadowDay != null ? `Day ${day.shadowDay}` : 'Shadow day';
  const panelId = `score-day-${dayKey(day)}`;
  const onScale = formatOnScale(day.onScale);
  return (
    <>
      <tr className={day.date === today ? 'bg-primary-50/50' : ''}>
        <td className="whitespace-nowrap px-4 py-2.5 align-top">
          <div className="flex items-start gap-1.5">
            {canExpand ? (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={panelId}
                aria-label={`${open ? 'Hide' : 'Show'} the ratings for ${label}`}
                className="-ml-1 mt-0.5 rounded p-0.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              >
                <ChevronRight className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} />
              </button>
            ) : (
              <span className="w-4 shrink-0" aria-hidden="true" />
            )}
            <div>
              <div className="font-medium text-gray-900">
                {label}
                <span className="ml-1.5 text-xs font-normal text-gray-500">{shortDate(day.date)}</span>
                {day.date === today && (
                  <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">Today</span>
                )}
              </div>
              <div className="text-[11px] text-gray-400">Journey day {day.journeyDay}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-2.5 align-top">
          <div className="text-gray-900">
            {day.formTitle ?? <span className="text-gray-400">No sign-off form</span>}
            {day.filedLate && (
              <span
                title="Filed after the day itself (a catch-up or a back-fill)"
                className="ml-1.5 whitespace-nowrap rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
              >
                Filed late
              </span>
            )}
          </div>
          {day.buddyName && <div className="text-[11px] text-gray-400">Buddy {day.buddyName}</div>}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 align-top">
          {day.state === 'rated' ? (
            <>
              <div className="font-semibold text-gray-900">
                {onScale ? `${onScale} · ${formatScore(day.pct, passMark)}` : formatScore(day.pct, passMark)}
              </div>
              <div className="text-[11px] text-gray-400">{plural(day.ratedCount, 'rating')}</div>
            </>
          ) : (
            <span className="text-xs text-gray-400">{DAY_STATE_LABEL[day.state]}</span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 align-top">
          {day.band ? (
            <span className={`text-xs font-semibold ${BAND_META[day.band].text}`}>{BAND_META[day.band].label}</span>
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </td>
      </tr>
      {canExpand && open && (
        <tr id={panelId}>
          <td colSpan={4} className="bg-gray-50/60 px-4 pb-3 pt-1">
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              {questions.map((q) => (
                <li key={q.questionId} className="flex items-baseline justify-between gap-4 px-3 py-1.5 text-xs">
                  <span className={q.text ? 'text-gray-700' : 'italic text-gray-400'}>{q.text ?? 'Question removed'}</span>
                  <span className="shrink-0 whitespace-nowrap">
                    <span className="font-semibold text-gray-900">{`${fmtCount(q.value)} / ${q.max}`}</span>
                    <span className="ml-1.5 text-gray-400">{formatPct(q.pct)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The inline confirm step for a decision — kept inside the score modal rather
 * than a second dialog, so the evidence stays in view while the note is written.
 */
function DecisionPanel({ projectId, joiner, score, choice, planEnd, onCancel, onRecorded, onUnconfirmed }: {
  projectId: number;
  joiner: OnboardingScoreModalProps['joiner'];
  score: JoinerScore;
  choice: ScoreDecisionKind;
  /** The plan end as it stands now (a later extension included). */
  planEnd: string | null;
  onCancel: () => void;
  onRecorded: (r: DecisionResult) => void;
  /** The save may have landed but the reply was unusable — reload the history. */
  onUnconfirmed: () => void;
}) {
  const today = todayIst();
  const maxDate = addDaysIso(today, 90);
  // Same floor as the Extend action: the day after the current plan end (or today),
  // so an extension only ever pushes the plan LATER — an earlier date would
  // overwrite a previous extension and shorten the plan.
  const minDate = addDaysIso(planEnd && planEnd >= today ? planEnd : today, 1);
  // The later of a week past the current plan end and a week from today, never
  // beyond the 90-day limit the API enforces.
  const defaultDate = (() => {
    const week = addDaysIso(today, 7);
    const fromPlan = planEnd ? addDaysIso(planEnd, 7) : week;
    const later = fromPlan > week ? fromPlan : week;
    return later > maxDate ? maxDate : later;
  })();
  const [date, setDate] = useState(defaultDate);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reason = noteReason(choice, score.advice);
  const title = DECISION_ACTION[choice];

  const confirm = async () => {
    const trimmed = note.trim();
    if (reason && !trimmed) {
      setError('Add a note saying why before recording this decision.');
      return;
    }
    if (trimmed.length > MAX_DECISION_NOTE) {
      setError(`Keep the note to ${MAX_DECISION_NOTE} characters.`);
      return;
    }
    if (choice === 'extend' && (!date || date < minDate || date > maxDate)) {
      setError(`Pick a date from ${shortDate(minDate)} to ${shortDate(maxDate)}.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await onboardingAPI.recordScoreDecision({
        projectId,
        employeeCode: joiner.employeeCode,
        dateOfJoining: joiner.dateOfJoining,
        planEndDate: joiner.planEndDate,
        decision: choice,
        note: trimmed || undefined,
        extendedTo: choice === 'extend' ? date : undefined,
      });
      const result = res.data?.data as DecisionResult | undefined;
      if (!result?.decision || !result?.score) {
        // Decisions are append-only, so a blind retry could record it twice.
        setError('The decision could not be confirmed. Check the history before trying again.');
        onUnconfirmed();
        return;
      }
      onRecorded(result);
    } catch (err) {
      setError(apiMessage(err, 'Could not record the decision. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div role="group" aria-label={title} className="space-y-3 rounded-xl border border-primary-100 bg-primary-50/40 p-4">
      <div>
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        <p className="mt-0.5 text-xs text-gray-600">
          {choice === 'deploy'
            ? `Records that ${joiner.employeeName} is cleared for deployment. Nothing else changes.`
            : `Records the decision and extends ${joiner.employeeName}’s onboarding plan.`}
        </p>
      </div>

      {choice === 'extend' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="score-extend-date">Extend onboarding until</label>
          <input
            id="score-extend-date"
            type="date"
            value={date}
            min={minDate}
            max={maxDate}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 sm:w-56"
          />
          <p className="mt-1.5 flex items-start gap-1.5 text-xs text-gray-500">
            <CalendarPlus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span>Their onboarding plan runs until this date. A finished plan moves back to the <strong>Active</strong> tab.</span>
          </p>
        </div>
      )}

      <div>
        <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600" htmlFor="score-decision-note">
          Note
          {reason
            ? <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Required</span>
            : <span className="font-normal text-gray-400">(optional)</span>}
        </label>
        {reason && <p className="mb-1.5 text-xs text-amber-800">{reason}</p>}
        <textarea
          id="score-decision-note"
          rows={3}
          value={note}
          maxLength={MAX_DECISION_NOTE}
          aria-required={reason ? true : undefined}
          onChange={(e) => setNote(e.target.value)}
          placeholder={choice === 'deploy' ? 'Anything the deployment team should know?' : 'Why is onboarding being extended?'}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={saving}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {saving ? 'Recording…' : 'Record decision'}
        </button>
      </div>
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────

export default function OnboardingScoreModal({ projectId, joiner, onClose, onDecided }: OnboardingScoreModalProps) {
  const { employeeCode, dateOfJoining, planEndDate } = joiner;
  const [data, setData] = useState<JoinerScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [choice, setChoice] = useState<ScoreDecisionKind | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Every fetch takes a ticket; only the newest may write, so a slow load can
  // never overwrite the refresh that followed a decision (or the reverse).
  const fetchSeq = useRef(0);

  const fetchDetail = () =>
    onboardingAPI.getScoreDetail({ projectId, employeeCode, dateOfJoining, planEndDate })
      .then((res) => (res.data?.data ?? null) as JoinerScore | null);

  useEffect(() => {
    let cancelled = false;
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError(null);
    fetchDetail()
      .then((score) => {
        if (!cancelled && seq === fetchSeq.current) setData(score);
      })
      .catch((err) => {
        if (!cancelled && seq === fetchSeq.current) setError(apiMessage(err, 'Could not load the onboarding score.'));
      })
      .finally(() => {
        if (!cancelled && seq === fetchSeq.current) setLoading(false);
      });
    return () => { cancelled = true; };
    // fetchDetail reads only these values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, employeeCode, dateOfJoining, planEndDate, reloadKey]);

  /** Re-read the detail without the loading state; on failure keep what is shown. */
  const refreshQuietly = () => {
    const seq = ++fetchSeq.current;
    fetchDetail()
      .then((score) => {
        if (seq === fetchSeq.current && score) setData(score);
      })
      .catch(() => { /* the decision's own score is already on screen */ });
  };

  const handleRecorded = (result: DecisionResult) => {
    setChoice(null);
    setData((prev) => mergeDecided(prev, result));
    setNotice(
      result.decision.decision === 'deploy'
        ? 'Decision recorded: cleared for deployment.'
        : `Decision recorded: onboarding extended${result.decision.extendedTo ? ` to ${shortDate(result.decision.extendedTo)}` : ''}.`,
    );
    refreshQuietly();
    onDecided(result);
  };

  const toggleDay = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const today = todayIst();
  const s = data;
  const complete = s?.trainingState === 'complete';
  const advised: ScoreDecisionKind | null = s?.advice === 'deploy' || s?.advice === 'extend' ? s.advice : null;
  const decisions = [...(s?.decisions ?? [])].sort((a, b) => b.decidedAt.localeCompare(a.decidedAt) || b.id - a.id);
  // The plan end as it stands: an extension recorded here moves it even before
  // the worklist row refreshes.
  const lastExtension = decisions.find((d) => d.decision === 'extend' && d.extendedTo)?.extendedTo ?? null;
  const planEnd = lastExtension && (!planEndDate || lastExtension > planEndDate) ? lastExtension : planEndDate;

  const renderBody = () => {
    if (loading) {
      return <div className="rounded-xl border border-gray-200 py-10 text-center text-sm text-gray-400"><InlineLoader label="Loading the onboarding score…" className="flex justify-center" /></div>;
    }
    if (error) {
      return (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
          >
            Try again
          </button>
        </div>
      );
    }
    if (!s) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
          There is no onboarding score for this joiner.
        </div>
      );
    }

    const advicePill = scorePill({ ...s, latestDecision: null });
    const decidedPill = s.latestDecision ? scorePill(s) : null;
    const toCome = Math.max(0, s.daysExpected - s.daysScorable);
    const atPass = s.scorePct != null && Math.round(s.scorePct * 10) / 10 >= s.passMark;
    const showTopics = s.topics.some((t) => t.days > 1);

    return (
      <>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label="Overall">
            <div className="text-3xl font-bold leading-tight text-gray-900">{formatScore(s.scorePct, s.passMark)}</div>
            <div className={`text-xs ${s.scorePct == null ? 'text-gray-400' : atPass ? 'text-emerald-700' : 'text-amber-700'}`}>
              {s.scorePct == null
                ? 'No day rated yet'
                : `${atPass ? 'At or above' : 'Below'} the pass mark${complete ? '' : ' so far'}`}
            </div>
          </Tile>
          <Tile label="Pass mark">
            <div className="text-xl font-bold text-gray-900">{s.passMark}%</div>
            <div className="text-xs text-gray-500">Set for this project</div>
          </Tile>
          <Tile label="Days rated">
            {s.daysExpected === 0 ? (
              <>
                <div className="text-xl font-bold text-gray-300">—</div>
                <div className="text-xs text-gray-400">
                  {s.trainingState === 'no_training' ? 'No shadow training yet' : 'No day asks for ratings'}
                </div>
              </>
            ) : (
              <>
                <div className="text-xl font-bold text-gray-900">{s.daysRated} / {s.daysExpected}</div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                  {s.daysMissed > 0 && (
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">{s.daysMissed} missed</span>
                  )}
                  {toCome > 0 && <span>{toCome} still to come</span>}
                  {s.daysMissed === 0 && toCome === 0 && <span>None missed</span>}
                </div>
              </>
            )}
          </Tile>
          <Tile label="Advice">
            <Pill tone={advicePill.tone} title={advicePill.title}>{advicePill.label}</Pill>
            <div className="mt-1 text-xs text-gray-500">
              {s.trainingState === 'in_progress' ? 'The score may change until the last shadow day.' : advicePill.title}
            </div>
            {decidedPill && (
              <div className="mt-1 text-xs text-gray-700">
                Decided: <span className="font-semibold">{decidedPill.label}</span>
              </div>
            )}
          </Tile>
        </div>

        {s.summary && <p className="text-sm text-gray-700">{s.summary}</p>}

        {s.days.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            {s.trainingState === 'no_training'
              ? 'No shadow training is on record for this joiner, so there is nothing to score yet.'
              : 'No sign-off day to show yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500">
                <tr>
                  <th className="whitespace-nowrap px-4 py-2.5">Shadow day</th>
                  <th className="px-4 py-2.5">Topic</th>
                  <th className="whitespace-nowrap px-4 py-2.5">Day score</th>
                  <th className="whitespace-nowrap px-4 py-2.5">Reads as</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {s.days.map((d) => (
                  <DayRows
                    key={dayKey(d)}
                    day={d}
                    passMark={s.passMark}
                    today={today}
                    open={expanded.has(dayKey(d))}
                    onToggle={() => toggleDay(dayKey(d))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showTopics && (
          <section>
            <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">By topic</h4>
            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
              {s.topics.map((t) => (
                <li key={`${t.formId ?? 'none'}-${t.title}`} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                  <span className="min-w-0 text-gray-800">
                    {t.title}
                    <span className="ml-1.5 text-xs text-gray-400">{plural(t.days, 'day')}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <span className="font-semibold text-gray-900">{formatScore(t.pct, s.passMark)}</span>
                    <span className={`w-20 text-right text-xs font-semibold ${BAND_META[t.band].text}`}>{BAND_META[t.band].label}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {decisions.length > 0 && (
          <section>
            <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Decision history</h4>
            <ol className="space-y-2">
              {decisions.map((d) => (
                <li key={d.id} className="rounded-xl border border-gray-200 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Pill tone={d.decision === 'deploy' ? 'success' : 'warning'}>{decisionHeadline(d)}</Pill>
                    <span className="text-xs text-gray-500">{fmtDecidedOn(d.decidedAt)} · by {d.decidedBy}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {`Score ${formatScore(d.scorePct, d.passMark)} vs pass mark ${d.passMark}%`}
                    {` · ${d.daysRated} of ${d.daysScorable} days rated`}
                    {d.advice && d.advice !== d.decision ? ` · advice was ${ADVICE_META[d.advice].label}` : ''}
                  </div>
                  {d.note && <p className="mt-1.5 whitespace-pre-line text-sm text-gray-700">{d.note}</p>}
                </li>
              ))}
            </ol>
          </section>
        )}

        {notice && (
          <div role="status" className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {notice}
          </div>
        )}

        <div className="border-t border-gray-100 pt-4">
          {!complete ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {s.lastShadowDate
                  ? `You can record a decision once the last shadow day (${shortDate(s.lastShadowDate)}) is over.`
                  : 'You can record a decision once shadow training is over.'}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          ) : choice ? (
            <DecisionPanel
              projectId={projectId}
              joiner={joiner}
              score={s}
              choice={choice}
              planEnd={planEnd}
              onCancel={() => setChoice(null)}
              onRecorded={handleRecorded}
              onUnconfirmed={refreshQuietly}
            />
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {s.latestDecision ? 'A new decision is added to the history; earlier ones stay.' : ''}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {(['deploy', 'extend'] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => { setNotice(null); setChoice(kind); }}
                    title={advised === kind ? 'The score advises this' : undefined}
                    className={advised === kind
                      ? 'rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700'
                      : 'rounded-lg border border-primary-200 bg-white px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50'}
                  >
                    {DECISION_ACTION[kind]}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal title="Onboarding score" onClose={onClose} maxWidth="max-w-5xl">
      <div className="space-y-4">
        <div>
          <div className="text-base font-semibold text-gray-900">
            {joiner.employeeName} <span className="font-normal text-gray-500">· {employeeCode}</span>
          </div>
          <div className="text-xs text-gray-500">
            Joined {shortDate(dateOfJoining)}
            {planEnd ? ` · plan ${planEnd < today ? 'ended' : 'ends'} ${shortDate(planEnd)}` : ''}
          </div>
        </div>
        {renderBody()}
      </div>
    </Modal>
  );
}
